import Asset from 'models/Asset';
import CallWithParticipants, { CallEndReason, OngoingCall } from 'models/Call';
import { ChatroomTokenPayload, ChatroomTokenProps } from 'models/Chat';
import DonationDetails from 'models/DonationDetails';
import Event, { ContentBlock } from 'models/Event';
import EventHost from 'models/EventHost';
import EventQueue from 'models/EventQueue';
import EventReport from 'models/EventReport';
import Fan from 'models/Fan';
import HostInvitation from 'models/HostInvitation';
import LiveStream, {
  LiveStreamAdmin,
  LiveStreamMetrics,
  LiveStreamStatus,
  LiveStreamStatusAction,
} from 'models/LiveStream';
import Session from 'models/Session';
import TeamMember from 'models/TeamMember';
import TicketPackage from 'models/TicketPackage';

//Keep in mind while running locally: This endpoint points to virtual-events-admin stack
const ADMIN_API = `https://api.${process.env.DOMAIN}/admin`;
const API_ENDPOINT = `${process.env.API_HOST}`;

export async function getSession(): Promise<Session> {
  const session = await callApi('GET', '/api/session');
  return session;
}

type TwilioParticipant = {
  sid: string;
  roomSid: string;
  identity: string;
  status: string;
  duration: number;
  startTime: string;
  endTime: string;
};

export interface DashboardRoomResponse {
  roomName: string;
  accessToken: string;
  participants: TwilioParticipant[] | null;
}

export async function getDashboardRoom(): Promise<DashboardRoomResponse> {
  return await callApi('GET', '/api/dashboard/room');
}

export async function artistLogin(
  email: string,
  password: string,
  displayName: string,
): Promise<Session> {
  return await callApi('POST', '/api/host/login', {
    displayName,
    email,
    password,
  });
}

export async function artistSignup(
  email: string,
  password: string,
  displayName: string,
): Promise<Session> {
  return await callApi('POST', '/api/host/signup', {
    displayName,
    email,
    password,
  });
}

export async function artistResetPassword(email: string) {
  return await callApi('POST', '/api/host/password', {
    email,
  });
}

export async function artistUpdatePassword(
  password: string,
  token: string,
): Promise<Session> {
  return await callApi('PUT', '/api/host/password', {
    password,
    token,
  });
}

export async function artistAcceptInvitation(
  token: string,
  email: string,
  password: string,
  displayName: string,
): Promise<Session> {
  return await callApi('POST', '/api/host/accept/' + token, {
    displayName,
    email,
    password,
  });
}

interface FanVerifyResponse {
  valid: boolean;
  fan: Fan;
  event: Event;
}

export async function fanVerifyInvitation(
  pin: string,
): Promise<FanVerifyResponse> {
  const result = await callApi('POST', '/api/fan/verify', {
    pin,
  });
  return result;
}

export async function fanAcceptInvitation(
  slug: string,
  pin: string,
  email: string,
  firstName: string,
  lastName: string,
  marketing: boolean,
  phoneNumber?: string,
): Promise<Session> {
  return await callApi('POST', '/api/fan/join', {
    slug,
    pin,
    email,
    firstName,
    lastName,
    marketing,
    phoneNumber,
  });
}

// TODO: these point to the new stream handler.  They need to be reconciled with
// the existing endpoints at some point.

export interface StreamEvent {
  eventId: number;
  status: LiveStreamStatus;
  slug: string;
  displayName: string;
  description: string;
  startsAt: Date;
  startsAtTimeZone: string;
  backgroundDigest: string;
  bannerDigest: string;
  chatEnabled: boolean;
  merchEnabled: boolean;
  audienceQuestionsEnabled: boolean;
  streamPlaybackID: string;
  assetPlaybackID: string;
  assetExpiresAt?: Date;
  donationDetails: DonationDetails;
  customTerms: string;
  announcement?: string;
  announcementTitle?: string;
  chatrooms?: number;
}

export interface StreamInvitation {
  pin: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  marketing: boolean;
}

interface StreamResponse {
  event: StreamEvent;
  invitation: StreamInvitation;
}

export interface StreamLoggedInResponse extends StreamResponse {
  chatToken: string;
  websocketToken: string;
  sessionToken: string;
}

export async function streamValidatePin(pin: string): Promise<StreamResponse> {
  return await callApi('POST', '/api/stream/validate', {
    pin,
  });
}

export async function streamFanLogin(
  pin: string,
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  marketing: boolean,
): Promise<StreamLoggedInResponse> {
  return await callApi('POST', '/api/stream/login', {
    pin,
    firstName,
    lastName,
    email,
    phone,
    marketing,
  });
}

export async function streamGetEvent(): Promise<StreamLoggedInResponse> {
  return await callApi('GET', '/api/stream/event');
}

// END TODO

export async function getPretestUrls(): Promise<any> {
  return await callApi('GET', '/api/speed-test/pre-sign');
}

export async function getPretestCallDetails(): Promise<any> {
  return await callApi('GET', '/api/call-test/initialize');
}

export async function logout(): Promise<void> {
  return await callApi('POST', '/api/logout');
}

export async function createEvent(
  title: string,
  slug: string,
  durationMinutes: number,
  callDurationSeconds: number,
  invitationCount: number,
  startsAt: string,
  timeZone: string,
  disableCallVideos: boolean,
  merchCurrency: string,
  announcement: string,
  announcementTitle: string,
  chatrooms: number,
  copyFrom?: number,
): Promise<Event> {
  // If they specified copyFrom, include it as a query parameter to the POST
  const qp = copyFrom ? `?copyFrom=${copyFrom}` : ``;
  const response = await callApi('POST', `/api/events${qp}`, {
    slug,
    title,
    durationMinutes,
    callDurationSeconds,
    invitationCount,
    startsAt,
    timeZone,
    disableCallVideos,
    merchCurrency,
    announcement,
    announcementTitle,
    chatrooms,
  });
  return response.event;
}

export async function updateEvent(event: Event): Promise<Event> {
  const response = await callApi('PUT', `/api/events/${event.id}`, event);
  return response.event;
}

export async function getEvent(slug: string): Promise<Event> {
  const response = await callApi('GET', `/api/event/${slug}`);
  return response.event;
}

export async function getEventById(id: string): Promise<Event> {
  const response = await callApi('GET', `/api/events/${id}`);
  return response.event;
}

export async function deleteEvent(eventId: string): Promise<any> {
  return await callApi('DELETE', `/api/events/${eventId}`);
}

export async function getEvents(): Promise<Event[]> {
  const response = await callApi('GET', `/api/events`);
  return response.events;
}

interface EventSelection {
  event: Event;
  eventHost: EventHost;
}

export async function selectEvent(eventId: number): Promise<EventSelection> {
  const response = await callApi('POST', `/api/events/select/${eventId}`);
  return response;
}

export async function deselectEvent(eventId: number): Promise<void> {
  return await callApi('DELETE', `/api/events/deselect/${eventId}`);
}

export async function getCurrentEvent(): Promise<EventQueue> {
  return await callApi('GET', '/api/event');
}

export async function createTeamInvitation(
  eventId: number,
  hostInvitation: HostInvitation,
  sendEmail: boolean,
): Promise<any> {
  return await callApi('POST', '/api/host/invite', {
    eventId,
    ...hostInvitation,
    sendEmail: !!sendEmail,
  });
}

export async function getPendingEventTeamInvitations(
  eventId: number,
): Promise<HostInvitation[]> {
  return await callApi('GET', `/api/events/${eventId}/team/invitations`);
}

export async function updatePendingEventTeamInvitation(
  eventId: number,
  hostInvitation: HostInvitation,
): Promise<void> {
  const { id: hostInvitationId, ..._invitation } = hostInvitation;
  return await callApi(
    'PUT',
    `/api/events/${eventId}/team/invitations/${hostInvitationId}`,
    _invitation,
  );
}

export async function createEventInvitations(eventId: number, fans: Fan[]) {
  return await callApi('POST', `/api/events/${eventId}/invitations`, fans);
}

export async function getEventInvitations(eventId: number) {
  return await callApi('GET', `/api/events/${eventId}/invitations`);
}

export async function deleteEventInvitations(
  eventId: string,
  invitationIds: number[],
): Promise<any> {
  return await callApi(
    'DELETE',
    `/api/events/${eventId}/invitations`,
    invitationIds,
  );
}

export async function getEventReport(eventId: number): Promise<any> {
  const resp = await fetch(ADMIN_API + `/event/${eventId}/report`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'x-sp-auth': `${process.env.EVENT_REPORT_PASSWORD}`,
    },
  });
  if (
    resp.status !== 204 &&
    resp.headers.get('Content-Type') == 'application/json'
  ) {
    const response = parseJSON(await resp.text());
    return response as EventReport;
  }
}

export async function sendEventInvitationEmails(
  eventId: string,
  fanIds: number[],
): Promise<any> {
  return await callApi(
    'POST',
    `/api/events/${eventId}/invitation_emails`,
    fanIds,
  );
}

export async function addFanInvitations(
  eventId: string,
  invitationCount: number,
  meet: boolean = false,
  stream: boolean = false,
) {
  return await callApi('PUT', `/api/events/${eventId}/invitations`, {
    invitationCount,
    meet: meet ? 'true' : 'false',
    stream: stream ? 'true' : 'false',
  });
}

export async function deletePendingEventTeamInvitation(
  eventId: number,
  hostInvitationId: number,
): Promise<any> {
  return await callApi(
    'DELETE',
    `/api/events/${eventId}/team/invitations/${hostInvitationId}`,
  );
}

export async function getEventTeamMembers(
  eventId: number,
): Promise<TeamMember[]> {
  return await callApi('GET', `/api/events/${eventId}/team`);
}
export async function updateEventTeamMember(
  eventId: number,
  teamMember: TeamMember,
): Promise<void> {
  return await callApi(
    'PUT',
    `/api/events/${eventId}/team/${teamMember.hostId}`,
    teamMember,
  );
}
export async function deleteEventTeamMember(
  eventId: number,
  hostId: number,
): Promise<void> {
  return await callApi('DELETE', `/api/events/${eventId}/team/${hostId}`);
}

export async function callFan(
  eventFanId: number,
): Promise<{ roomToken: string }> {
  return await callApi('POST', `/api/dial/${eventFanId}`);
}

export async function cancelCallFan(eventFanId: number) {
  return await callApi('DELETE', `/api/dial/${eventFanId}`);
}

export async function bumpFan(eventFanId: number) {
  return await callApi('POST', `/api/bump/${eventFanId}`);
}

export async function answerCall(
  roomToken: string,
): Promise<CallWithParticipants> {
  return await callApi('POST', `/api/call/${roomToken}/answer`);
}

export async function updateCallTimer(
  roomToken: string,
  addTime: boolean,
): Promise<CallWithParticipants> {
  let path = `/api/call/${roomToken}/timer`;
  if (addTime) {
    path = path + '?extend';
  }
  return await callApi('POST', path);
}

export async function getCall(
  roomToken: string,
): Promise<CallWithParticipants> {
  return await callApi('GET', `/api/call/${roomToken}`);
}

export async function terminateCall(
  roomToken: string,
  reason = CallEndReason.Normal,
): Promise<OngoingCall> {
  return await callApi(
    'DELETE',
    `/api/call/${roomToken}?reason=${encodeURIComponent(reason)}`,
  );
}

export async function reportCallEnded(roomToken: string): Promise<OngoingCall> {
  return await callApi('DELETE', `/api/call/${roomToken}/ended`);
}

export async function takeoverCall(
  roomToken: string,
  hostId: number,
): Promise<CallWithParticipants> {
  return await callApi('POST', `/api/call/${roomToken}/host/${hostId}`);
}

export async function requestHelp(roomToken: string) {
  return await callApi('POST', `/api/call/${roomToken}/help`);
}

export async function cancelHelp(roomToken: string) {
  return await callApi('DELETE', `/api/call/${roomToken}/help`);
}

export async function requestPhoto(roomToken: string) {
  return await callApi('POST', `/api/photos/request/${roomToken}`);
}

export async function agreePhoto(
  roomToken: string,
): Promise<{ photoToken: string }> {
  return await callApi('POST', `/api/photos/agree/${roomToken}`);
}

export async function cancelPhoto(roomToken: string) {
  return await callApi('POST', `/api/photos/cancel/${roomToken}`);
}

export async function savePhoto(
  token: string,
  dataUrl: string,
  imageType: string,
) {
  return await callApi('POST', `/api/photos/save/${token}`, {
    image: dataUrl,
    imageType,
  });
}

export async function saveAsset(asset: Asset): Promise<{ name: string }> {
  return await callApi('POST', '/api/assets', asset);
}

export async function getContentBlocks(
  eventId: number,
): Promise<{ contentBlocks: ContentBlock[] }> {
  return await callApi('GET', `/api/events/${eventId}/content`);
}

export async function createContentBlock(eventId: number, block: ContentBlock) {
  return await callApi('POST', `/api/events/${eventId}/content`, [block]);
}

export async function updateContentBlock(eventId: number, block: ContentBlock) {
  return await callApi(
    'PUT',
    `/api/events/${eventId}/content/${block.id}`,
    block,
  );
}

export async function createOrUpdateContentBlock(
  eventId: number,
  block: ContentBlock,
) {
  if (block.id) {
    return updateContentBlock(eventId, block);
  } else {
    let result;
    try {
      result = await createContentBlock(eventId, block);
    } catch (err) {
      const { contentBlocks } = await getContentBlocks(eventId);
      block.id = contentBlocks.find(
        (b) => b.screen === block.screen && b.position === block.position,
      )?.id;
      result = await updateContentBlock(eventId, block);
    }
    return result;
  }
}

export async function getChatroomToken({ eventId }: ChatroomTokenProps) {
  const resp = await callApi('POST', `/api/chat/${eventId}`);
  return resp as ChatroomTokenPayload;
}

export async function getLiveStreamPlayback(
  slug: string,
): Promise<{ event: Event; liveStream: LiveStream }> {
  const response = await callApi('GET', `/api/streaming/${slug}/playback`);
  return response as { event: Event; liveStream: LiveStream };
}

export async function getLiveStreamAdmin(
  eventId: number,
): Promise<LiveStreamAdmin> {
  const response = await callApi('GET', `/api/streaming/${eventId}/admin`);
  return response as LiveStreamAdmin;
}

export async function getLiveStreamMetrics(
  eventId: number,
): Promise<LiveStreamMetrics> {
  const response = await callApi('GET', `/api/streaming/${eventId}/metrics`);
  return response as LiveStreamMetrics;
}

export async function setLiveStreamEnabled(
  eventId: number,
  enabled: boolean,
): Promise<LiveStreamAdmin> {
  const response = await callApi('PUT', `/api/streaming/${eventId}/enable`, {
    enabled,
  });
  return response as LiveStreamAdmin;
}

export async function setLiveStreamState(
  eventId: number,
  action: LiveStreamStatusAction,
): Promise<LiveStreamAdmin> {
  const response = await callApi('PUT', `/api/streaming/${eventId}/state`, {
    action,
  });
  return response as LiveStreamAdmin;
}

export async function resetLiveStreamStreamingKey(
  eventId: number,
): Promise<LiveStreamAdmin> {
  const response = await callApi('POST', `/api/streaming/${eventId}/reset`);
  return response as LiveStreamAdmin;
}

export async function initiateDemoStream(eventId: number) {
  await callApi('POST', `/api/streaming/${eventId}/demo`);
}

export async function updateLiveStream(
  eventId: number,
  liveStream: LiveStream,
): Promise<LiveStream> {
  const response = await callApi(
    'PUT',
    `/api/streaming/${eventId}`,
    liveStream,
  );
  return response.liveStream;
}

export async function createLiveStreamAssetUpload(
  eventId: number,
): Promise<string> {
  const response = await callApi('POST', `/api/streaming/${eventId}/upload`);
  return response.url;
}

export async function deleteLiveStreamRecording(
  eventId: number,
  recordingId: number,
) {
  await callApi(
    'DELETE',
    `/api/streaming/${eventId}/recordings/${recordingId}`,
  );
}

export async function setLiveStreamRecordingOnDemand(
  eventId: number,
  recordingId: number,
) {
  await callApi(
    'PUT',
    `/api/streaming/${eventId}/recordings/${recordingId}/publish`,
  );
}

export async function getTicketPackage(id: number): Promise<TicketPackage> {
  const response = await callApi('GET', `/api/ticketing/admin/${id}`);
  return response.ticketing;
}

export async function getAllTicketPackages(): Promise<TicketPackage[]> {
  const response = await callApi('GET', `/api/ticketing/admin`);
  return response.ticket_packages;
}

export async function createTicketPackage(
  details: TicketPackage,
): Promise<TicketPackage> {
  const response = await callApi('POST', '/api/ticketing', details);
  return response.ticketing;
}

export async function updateTicketPackage(
  packageId: number,
  details: TicketPackage,
): Promise<TicketPackage> {
  const response = await callApi('PUT', `/api/ticketing/${packageId}`, details);
  return response.ticketing;
}

export async function updateDonationDetails(
  eventId: number,
  details: DonationDetails,
): Promise<DonationDetails> {
  const response = await callApi('PUT', `/api/donations/${eventId}`, details);
  return response.donationDetails;
}

export async function associateCheckout(checkoutID: string) {
  return callApi('POST', '/api/checkouts/associate', {
    checkoutID,
  });
}

// Private

export async function callApi(
  method: string,
  path: string,
  body: object | undefined = undefined,
): Promise<any> {
  const maxAttempts = 7;
  let attempt = 0;
  while (true) {
    try {
      return await sendApiCall(method, path, body);
    } catch (e: any) {
      if (attempt < maxAttempts && [429, 503].includes(e?.details?.status)) {
        const delay = 2 ** attempt * 500;
        console.log('Server busy; retrying in', delay);
        await new Promise((resolve) => setTimeout(resolve, delay));
        attempt++;
      } else {
        throw e;
      }
    }
  }
}

async function sendApiCall(
  method: string,
  path: string,
  body: object | undefined = undefined,
): Promise<any> {
  const token = localStorage.getItem('TOKEN');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Client-Rev': process.env.GIT_VERSION || '',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const resp = await fetch(API_ENDPOINT + path, {
    method,
    body: body && JSON.stringify(body),
    credentials: 'include',
    headers: headers,
  });
  if (!resp.ok) {
    const text = await resp.text();

    if (resp.status === 400) {
      const json = JSON.parse(text);
      if (json.errors) {
        const validationErrors = json as {
          errors: ValidationErrors;
        };
        if (Object.keys(validationErrors).length > 0) {
          throw new ValidationError(
            'Validation failed',
            validationErrors.errors,
          );
        }
      } else if (json.length) {
        const bulkValidationErrors = json as BulkValidationErrorRow[];
        const err = new BulkValidationError(
          'Validation failed',
          bulkValidationErrors,
        );
        throw err;
      }
    }
    throw new APIError(method, path, resp.status, text);
  }

  if (
    resp.status !== 204 &&
    resp.headers.get('Content-Type') == 'application/json'
  ) {
    return parseJSON(await resp.text());
  }

  return null;
}

export interface ValidationErrors {
  [field: string]: string[];
}

export class ValidationError extends Error {
  errors: ValidationErrors;

  constructor(message: string, errors?: ValidationErrors) {
    super(message);
    this.errors = errors ?? {};
  }
}

export interface BulkValidationErrorRow {
  index: number;
  errors: ValidationErrors;
}

export class BulkValidationError extends Error {
  rows: BulkValidationErrorRow[];

  constructor(message: string, rows: BulkValidationErrorRow[]) {
    super(message);
    this.rows = rows;
  }
}

export class APIError extends Error {
  details: {
    method: string;
    path: string;
    status: number;
    body: string;
  };

  constructor(method: string, path: string, status: number, body: string) {
    super(`${status} ${method} ${path} ${body}`);
    this.name = 'APIError';
    this.details = {
      method,
      path,
      status,
      body,
    };
  }
}

export interface UnauthorizedError extends APIError {
  details: {
    method: string;
    path: string;
    status: 401;
    body: string;
  };
}

export function isUnauthorizedError(e: any): e is UnauthorizedError {
  return e?.details?.status === 401;
}

export function parseJSON(text: string): object {
  function dateReviver(key: string, value: object): object | null {
    if (key.match(/\w+At$/) && typeof value === 'string') {
      return new Date(value);
    }
    return value;
  }

  return JSON.parse(text, dateReviver);
}
